/**
 * Puente Órbitas → Conversación (plan 44, F1b).
 *
 * Gemelo del puente de Escuchar (`screens/listen/conversationBridge.ts`). El
 * feed abre la conversación EXISTENTE pasando las cards que el user acaba de
 * ver por `navigate('/conversation', { state: { short } })`. El controller de
 * conversación lee ese contexto y lo inyecta como `moduleHints.short` (JSON
 * string — el wsServer lo parsea), y el runtime de `languages` lo resuelve
 * contra el catálogo `shorts` e inyecta la card como FOCO de la charla.
 *
 * Viajan REFERENCIAS (`slug` + `cefr` de la variante que el user vio), no el
 * texto de la card: el contenido del system prompt sale siempre del catálogo.
 *
 * Dos entradas, una sola mecánica:
 *  - "Charlar de esto" (menú Más de una card) → UNA card.
 *  - "Usalo en una charla" (cierre de la órbita) → las cards de la órbita.
 */

import type { OrbitaContent } from './types.js';

/** Referencia a una card tal como viaja al backend. */
export interface ShortCardRef {
  readonly slug: string;
  readonly cefr: string;
}

/** Contexto que viaja por `location.state.short` al abrir la conversación. */
export interface ShortStartContext {
  readonly cards: readonly ShortCardRef[];
}

/**
 * Tope defensivo del payload del hint (el cupo de una órbita es 10; el backend
 * capea igual con `ORBITA_SESSION_SIZE`).
 */
const MAX_CARDS = 10;

/** Arma el contexto desde las cards que la UI ya tiene resueltas. */
export function buildShortStart(cards: readonly OrbitaContent[]): ShortStartContext | null {
  const refs = cards
    .filter((c) => c.slug.length > 0 && c.cefr.length > 0)
    .slice(0, MAX_CARDS)
    .map((c) => ({ slug: c.slug, cefr: c.cefr }));
  return refs.length > 0 ? { cards: refs } : null;
}

/**
 * Valida defensivamente el `location.state.short` (viene del router, no se
 * confía a ciegas). Devuelve el contexto tipado o null si está ausente/mal
 * formado (en cuyo caso la conversación arranca como charla libre normal).
 */
export function parseShortStart(state: unknown): ShortStartContext | null {
  if (!state || typeof state !== 'object') return null;
  const raw = (state as { short?: unknown }).short;
  if (!raw || typeof raw !== 'object') return null;
  const list = (raw as { cards?: unknown }).cards;
  if (!Array.isArray(list) || list.length === 0) return null;
  const cards: ShortCardRef[] = [];
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') return null;
    const o = entry as Record<string, unknown>;
    if (typeof o['slug'] !== 'string' || o['slug'].length === 0) return null;
    if (typeof o['cefr'] !== 'string' || o['cefr'].length === 0) return null;
    cards.push({ slug: o['slug'], cefr: o['cefr'] });
  }
  return { cards: cards.slice(0, MAX_CARDS) };
}
